import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Circle, ArrowRight, X, Compass, PartyPopper } from 'lucide-react'
import type { OnboardingProgress } from '@/types'

interface OnboardingChecklistProps {
  progress: OnboardingProgress
  onDismiss: () => void
  onOpenTour: () => void
}

/**
 * 新夥伴入職清單
 *
 * 與 TimerLockBanner 的差別：那個是「被擋下來才看到」，這個是一登入就看得到全貌，
 * 讓新人知道總共要做哪幾件事、目前到哪。全部完成後由 TimerPage 自動不再顯示。
 */
const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  progress,
  onDismiss,
  onOpenTour
}) => {
  const navigate = useNavigate()
  const { steps, completedCount, totalCount } = progress
  const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  // 第一個未完成的步驟＝現在該做的事
  const currentStepId = steps.find(s => !s.done)?.id

  return (
    <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200
      rounded-2xl p-5 animate-fade-in">
      {/* 標題列 */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-500/10
            flex items-center justify-center">
            <PartyPopper className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-primary-800">歡迎加入！先完成這幾件事</h3>
            <p className="text-sm text-primary-600">
              已完成 {completedCount} / {totalCount} 項
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onOpenTour}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
              text-primary-700 bg-white/70 hover:bg-white border border-primary-200 transition-all"
          >
            <Compass className="w-4 h-4" />
            <span className="hidden sm:inline">功能導覽</span>
          </button>
          <button
            onClick={onDismiss}
            aria-label="關閉入職清單"
            title="關閉（完成前可從功能導覽再次開啟）"
            className="p-1.5 rounded-lg text-primary-500 hover:text-primary-700
              hover:bg-white/70 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 進度條 */}
      <div className="h-2 rounded-full bg-white/70 overflow-hidden mb-4">
        <div
          className="h-full bg-primary-500 rounded-full transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* 步驟列表 */}
      <div className="rounded-xl overflow-hidden bg-white/60 border border-primary-100">
        {steps.map((step, index) => {
          const isCurrent = step.id === currentStepId

          return (
            <div
              key={step.id}
              className={`px-4 py-3 ${index !== 0 ? 'border-t border-primary-100' : ''} ${
                isCurrent ? 'bg-white/80' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  {step.done ? (
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-green-500 mt-0.5" />
                  ) : (
                    <Circle className="w-5 h-5 flex-shrink-0 text-primary-400 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-sm font-medium ${
                          step.done ? 'text-secondary-400 line-through' : 'text-secondary-800'
                        }`}
                      >
                        {step.label}
                      </span>
                      {/* note 描述的是「還差什麼」，完成後顯示會自相矛盾 */}
                      {!step.done && step.note && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full
                          text-primary-700 bg-primary-100">
                          {step.note}
                        </span>
                      )}
                    </div>
                    {!step.done && (
                      <p className="text-xs text-secondary-500 mt-0.5">{step.description}</p>
                    )}
                    {!step.done && step.items && step.items.length > 0 && (
                      <p className="text-xs text-secondary-600 mt-1">
                        {step.items.slice(0, 3).join('、')}
                        {step.items.length > 3 && ` 等 ${step.items.length} 項`}
                      </p>
                    )}
                  </div>
                </div>

                {!step.done && (
                  <button
                    onClick={() => navigate(step.ctaPath)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                      rounded-lg flex-shrink-0 transition-all shadow-sm hover:shadow
                      bg-primary-500 hover:bg-primary-600 text-white"
                  >
                    {step.ctaText}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default OnboardingChecklist
